// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AppIcon, Spinner, Dropdown, TablePagination, Button, Modal, TextArea, TextInput } from '@loa/ui-components';
import { adminApi } from '../api/adminApi';
import type {
  AdvisorWithAdmin,
  AdvisorListFilter,
  PageResponse,
  AdvisorDeactivateRequest,
  AdvisorInviteRequest,
} from '../types/admin';
import { useAdmin } from '../contexts/AdminContext';
import { useAdvisor } from '../contexts/AdvisorContext';

function statusToBackground(status: string): string {
  if (status === 'active') return '#21ba45';
  if (status === 'inactive') return '#db2828';
  return '#f2c037';
}

interface ReassignModalProps {
  open: boolean;
  advisor: AdvisorWithAdmin | null;
  advisors: AdvisorWithAdmin[];
  onClose: () => void;
  onConfirm: (targetAdvisorId: string, reason: string) => void;
}

interface InviteAdvisorModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (request: AdvisorInviteRequest) => void;
  brokerageId: string;
}

const InviteAdvisorModal: React.FC<InviteAdvisorModalProps> = ({
  open,
  onClose,
  onConfirm,
  brokerageId,
}) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    licenseNumber: '',
    role: 'USER' as 'ADMIN' | 'USER',
    notes: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleSubmit = () => {
    // Basic validation
    if (!formData.email || !formData.firstName || !formData.lastName) {
      setFormError('Email, first name, and last name are required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setFormError('Please enter a valid email address');
      return;
    }

    const request: AdvisorInviteRequest = {
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      brokerageId,
      phoneNumber: formData.phoneNumber || undefined,
      licenseNumber: formData.licenseNumber || undefined,
      role: formData.role,
      notes: formData.notes || undefined,
    };

    onConfirm(request);
    // Reset form
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      licenseNumber: '',
      role: 'USER',
      notes: '',
    });
    setFormError(null);
  };

  const handleClose = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      licenseNumber: '',
      role: 'USER',
      notes: '',
    });
    setFormError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size="small">
      <Modal.Header title="Invite New User" onClose={handleClose} />
      <Modal.Content>
        {formError && (
          <Alert variant="error">
            <p>{formError}</p>
          </Alert>
        )}
        <form>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '1rem' }}>
            <div className="form-field" style={{ flex: 1 }}>
              <span className="form-field__label">First Name *</span>
              <TextInput
                placeholder="First name"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
              />
            </div>
            <div className="form-field" style={{ flex: 1 }}>
              <span className="form-field__label">Last Name *</span>
              <TextInput
                placeholder="Last name"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
              />
            </div>
          </div>
          <div className="form-field" style={{ marginBottom: '1rem' }}>
            <span className="form-field__label">Email Address *</span>
            <TextInput
              type="email"
              placeholder="advisor@example.com"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
            />
          </div>
          <div className="form-field" style={{ marginBottom: '1rem' }}>
            <span className="form-field__label">Phone Number</span>
            <TextInput
              placeholder="+1 (555) 123-4567"
              value={formData.phoneNumber}
              onChange={(e) => handleChange('phoneNumber', e.target.value)}
            />
          </div>
          <div className="form-field" style={{ marginBottom: '1rem' }}>
            <label htmlFor="advisor-license-number">License Number</label>
            <TextInput
              id="advisor-license-number"
              placeholder="LIC-12345"
              value={formData.licenseNumber}
              onChange={(e) => handleChange('licenseNumber', e.target.value)}
            />
          </div>
          <div className="form-field" style={{ marginBottom: '1rem' }}>
            <label htmlFor="advisor-role">Role</label>
            <Dropdown
              id="advisor-role"
              options={[
                { text: 'User', value: 'USER' },
                { text: 'Admin', value: 'ADMIN' },
              ]}
              value={formData.role}
              onChange={(value) => handleChange('role', value)}
            />
          </div>
          <div className="form-field" style={{ marginBottom: '1rem' }}>
            <label htmlFor="advisor-notes">Notes</label>
            <TextArea
              id="advisor-notes"
              placeholder="Optional notes about this advisor..."
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </div>
        </form>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleClose }}
        primaryAction={{
          label: 'Send Invitation',
          onClick: handleSubmit,
          disabled: !formData.email || !formData.firstName || !formData.lastName,
        }}
      />
    </Modal>
  );
};

const ReassignModal: React.FC<ReassignModalProps> = ({
  open,
  advisor,
  advisors,
  onClose,
  onConfirm,
}) => {
  const [targetAdvisorId, setTargetAdvisorId] = useState('');
  const [reason, setReason] = useState('');

  const availableAdvisors = advisors
    .filter((a) => a.id !== advisor?.id && a.status === 'active')
    .map((a) => ({
      key: a.id,
      text: `${a.firstName} ${a.lastName} (${a.email})`,
      value: String(a.id),
    }));

  const handleConfirm = () => {
    if (targetAdvisorId) {
      onConfirm(targetAdvisorId, reason);
      setTargetAdvisorId('');
      setReason('');
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header title={`Deactivate Advisor: ${advisor?.firstName} ${advisor?.lastName}`} onClose={onClose} />
      <Modal.Content>
        <form>
          <div className="form-field" style={{ marginBottom: '1rem' }}>
            <span className="form-field__label">Reason for Deactivation *</span>
            <TextArea
              placeholder="Enter reason for deactivating this advisor..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          {advisor && advisor.profileCount > 0 && (
            <div className="form-field" style={{ marginBottom: '1rem' }}>
              <label>Reassign {advisor.profileCount} Relationship(s) To *</label>
              <Dropdown
                placeholder="Select advisor to receive relationships..."
                options={availableAdvisors.map(a => ({ value: a.value, text: a.text }))}
                value={targetAdvisorId}
                onChange={(value) => setTargetAdvisorId(value)}
              />
              <Alert variant="info">
                All relationships currently assigned to this advisor will be transferred
                to the selected advisor.
              </Alert>
            </div>
          )}
        </form>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: onClose }}
        primaryAction={{
          label: 'Deactivate Advisor',
          onClick: handleConfirm,
          colour: 'red',
          disabled: !reason || (advisor?.profileCount !== undefined && advisor.profileCount > 0 && !targetAdvisorId),
        }}
      />
    </Modal>
  );
};

const AdvisorManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { advisor: currentAdvisor } = useAdvisor();

  const [advisors, setAdvisors] = useState<AdvisorWithAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Filters
  const [filter, setFilter] = useState<AdvisorListFilter>({
    search: '',
    status: 'all',
    role: 'all',
  });

  // Modals
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [selectedAdvisor, setSelectedAdvisor] = useState<AdvisorWithAdmin | null>(null);
  const [confirmRoleChange, setConfirmRoleChange] = useState<{
    open: boolean;
    advisor: AdvisorWithAdmin | null;
    newRole: boolean;
  }>({ open: false, advisor: null, newRole: false });
  const [confirmReactivate, setConfirmReactivate] = useState<{
    open: boolean;
    advisor: AdvisorWithAdmin | null;
  }>({ open: false, advisor: null });
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const loadAdvisors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response: PageResponse<AdvisorWithAdmin> = await adminApi.listAdvisors(
        filter,
        page,
        pageSize
      );

      setAdvisors(response.content);
      setTotalPages(response.totalPages);
    } catch (err) {
      console.error('Failed to load advisors:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filter, page, pageSize]);

  useEffect(() => {
    if (!adminLoading && isAdmin) {
      loadAdvisors();
    } else if (!adminLoading && !isAdmin) {
      navigate('/');
    }
  }, [adminLoading, isAdmin, loadAdvisors, navigate]);

  const handleSearch = (value: string) => {
    setFilter((prev) => ({ ...prev, search: value }));
    setPage(0);
  };

  const handleStatusFilter = (status: 'active' | 'inactive' | 'invited' | 'all') => {
    setFilter((prev) => ({ ...prev, status }));
    setPage(0);
  };

  const handleRoleFilter = (role: 'admin' | 'user' | 'all') => {
    setFilter((prev) => ({ ...prev, role }));
    setPage(0);
  };

  const handleRoleChange = async (advisor: AdvisorWithAdmin, newRole: boolean) => {
    try {
      setError(null);
      await adminApi.setAdvisorAdminRole(String(advisor.id), {
        isAdmin: newRole,
        reason: newRole ? 'Granted admin role' : 'Revoked admin role',
      });
      setSuccessMessage(
        `Successfully ${newRole ? 'granted' : 'revoked'} admin role for ${advisor.firstName} ${advisor.lastName}`
      );
      loadAdvisors();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update advisor role';
      if (errorMessage.includes('last admin')) {
        setError('Cannot remove admin role from the last admin in the brokerage.');
      } else {
        setError('Failed to update advisor role. Please try again.');
      }
    } finally {
      setConfirmRoleChange({ open: false, advisor: null, newRole: false });
    }
  };

  const handleDeactivate = async (targetAdvisorId: string, reason: string) => {
    if (!selectedAdvisor) return;

    try {
      setError(null);
      const request: AdvisorDeactivateRequest = {
        reason,
        reassignToAdvisorId: targetAdvisorId || undefined,
      };
      await adminApi.deactivateAdvisor(String(selectedAdvisor.id), request);
      setSuccessMessage(
        `Successfully deactivated ${selectedAdvisor.firstName} ${selectedAdvisor.lastName}`
      );
      setDeactivateModalOpen(false);
      setSelectedAdvisor(null);
      loadAdvisors();
    } catch (err) {
      console.error('Failed to deactivate advisor:', err);
      setError('Failed to deactivate advisor. Please try again.');
    }
  };

  const handleReactivate = async (advisor: AdvisorWithAdmin) => {
    try {
      setError(null);
      await adminApi.reactivateAdvisor(String(advisor.id));
      setSuccessMessage(
        `Successfully reactivated ${advisor.firstName} ${advisor.lastName}`
      );
      loadAdvisors();
    } catch (err) {
      console.error('Failed to reactivate advisor:', err);
      setError('Failed to reactivate advisor. Please try again.');
    } finally {
      setConfirmReactivate({ open: false, advisor: null });
    }
  };

  const openDeactivateModal = (advisor: AdvisorWithAdmin) => {
    setSelectedAdvisor(advisor);
    setDeactivateModalOpen(true);
  };

  const handleInviteAdvisor = async (request: AdvisorInviteRequest) => {
    try {
      setError(null);
      await adminApi.inviteAdvisor(request);
      setSuccessMessage(
        `Successfully invited ${request.firstName} ${request.lastName} to join the brokerage`
      );
      setInviteModalOpen(false);
      loadAdvisors();
    } catch (err) {
      console.error('Failed to invite advisor:', err);
      setError('Failed to invite advisor. Please try again.');
    }
  };


  if (adminLoading || (loading && advisors.length === 0)) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <Spinner />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <Alert variant="error" header="Access Denied">
          <p>You do not have permission to manage users.</p>
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ padding: '2em 3em' }}>
      {/* Breadcrumb */}
      <nav style={{ marginBottom: '1em', fontSize: '0.9em', color: '#666' }}>
        <button
          type="button"
          style={{ cursor: 'pointer', color: '#4169E1', background: 'transparent', border: 0, font: 'inherit', padding: 0 }}
          onClick={() => navigate('/')}
        >Dashboard</button>
        {' / '}
        <button
          type="button"
          style={{ cursor: 'pointer', color: '#4169E1', background: 'transparent', border: 0, font: 'inherit', padding: 0 }}
          onClick={() => navigate('/admin/dashboard')}
        >Admin</button>
        {' / '}
        <span>Manage Users</span>
      </nav>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <AppIcon name="users" />
        <h1 style={{ margin: 0 }}>Manage Users</h1>
      </div>
      <p style={{ color: '#666', marginBottom: '1rem' }}>Manage all users across the brokerage</p>

      {error && (
        <Alert variant="error" header="Error" dismissible onDismiss={() => setError(null)}>
          <p>{error}</p>
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" header="Success" dismissible onDismiss={() => setSuccessMessage(null)}>
          <p>{successMessage}</p>
        </Alert>
      )}

      {/* Filters */}
      <div style={{ padding: '1rem', background: '#f9f9f9', borderRadius: '4px', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '1em', flexWrap: 'wrap', alignItems: 'center' }}>
          <TextInput
            iconLeft={<AppIcon name="search" size={16} color="#333333" />}
            placeholder="Search users..."
            value={filter.search}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ minWidth: '200px' }}
          />
          <Dropdown
            placeholder="Status"
            options={[
              { text: 'All Statuses', value: 'all' },
              { text: 'Active', value: 'active' },
              { text: 'Inactive', value: 'inactive' },
              { text: 'Invited', value: 'invited' },
            ]}
            value={filter.status}
            onChange={(value) => handleStatusFilter(value as 'active' | 'inactive' | 'invited' | 'all')}
          />
          <Dropdown
            placeholder="Role"
            options={[
              { text: 'All Roles', value: 'all' },
              { text: 'Admins Only', value: 'admin' },
              { text: 'Users Only', value: 'user' },
            ]}
            value={filter.role}
            onChange={(value) => handleRoleFilter(value as 'admin' | 'user' | 'all')}
          />
          <Button variant="outlined" onClick={loadAdvisors}>
            <AppIcon name="refresh" />
          </Button>
          <div style={{ flex: 1 }} />
          <Button variant="filled" onClick={() => setInviteModalOpen(true)}>
            <AppIcon name="user plus" />
            Invite User
          </Button>
        </div>
      </div>

      {/* Advisors Table */}
      <table className="fin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Status</th>
            <th>Role</th>
            <th>Relationships</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {advisors.map((advisor) => (
            <tr key={advisor.id}>
              <td>
                {advisor.firstName} {advisor.lastName}
                {advisor.id === currentAdvisor?.id && (
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.75em', background: '#2185d0', color: 'white', padding: '2px 6px', borderRadius: '3px' }}>
                    You
                  </span>
                )}
              </td>
              <td>{advisor.email}</td>
              <td>
                <span style={{ padding: '2px 8px', borderRadius: '3px', fontSize: '0.85em', background: statusToBackground(advisor.status), color: 'white' }}>
                  {advisor.status}
                </span>
              </td>
              <td>
                {advisor.isAdmin ? (
                  <span style={{ padding: '2px 8px', borderRadius: '3px', fontSize: '0.85em', background: '#a333c8', color: 'white' }}>
                    <AppIcon name="star" /> Admin
                  </span>
                ) : (
                  <span style={{ padding: '2px 8px', borderRadius: '3px', fontSize: '0.85em', background: '#e0e0e0', color: '#333' }}>User</span>
                )}
              </td>
              <td>{advisor.profileCount || 0}</td>
              <td>
                {advisor.id !== currentAdvisor?.id && (
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {advisor.status === 'active' && (
                      <>
                        <Button
                          variant="outlined"
                          size="sm"
                          onClick={() => setConfirmRoleChange({ open: true, advisor, newRole: !advisor.isAdmin })}
                        >
                          {advisor.isAdmin ? 'Remove Admin' : 'Grant Admin'}
                        </Button>
                        <Button
                          variant="outlined"
                          size="sm"
                          colour="red"
                          onClick={() => openDeactivateModal(advisor)}
                        >
                          Deactivate
                        </Button>
                      </>
                    )}
                    {advisor.status === 'inactive' && (
                      <Button
                        variant="outlined"
                        size="sm"
                        colour="green"
                        onClick={() => setConfirmReactivate({ open: true, advisor })}
                      >
                        Reactivate
                      </Button>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
          {advisors.length === 0 && (
            <tr>
              <td colSpan={6} style={{ textAlign: 'center' }}>
                No users found matching your criteria.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <TablePagination
          config={{
            currentPage: page + 1,
            totalPages,
            itemsPerPage: pageSize,
            itemsPerPageOptions: [10, 20, 50],
            onPageChange: (next) => setPage(next - 1),
            onItemsPerPageChange: (size) => {
              setPageSize(size);
              setPage(0);
            },
          }}
        />
      )}

      {/* Invite Advisor Modal */}
      <InviteAdvisorModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onConfirm={handleInviteAdvisor}
        brokerageId={String(currentAdvisor?.brokerageId || '')}
      />

      {/* Deactivate Modal */}
      <ReassignModal
        open={deactivateModalOpen}
        advisor={selectedAdvisor}
        advisors={advisors}
        onClose={() => {
          setDeactivateModalOpen(false);
          setSelectedAdvisor(null);
        }}
        onConfirm={handleDeactivate}
      />

      {/* Role Change Confirmation */}
      <Modal
        open={confirmRoleChange.open}
        onClose={() => setConfirmRoleChange({ open: false, advisor: null, newRole: false })}
        size="small"
      >
        <Modal.Header
          title={confirmRoleChange.newRole ? 'Grant Admin Role' : 'Remove Admin Role'}
          onClose={() => setConfirmRoleChange({ open: false, advisor: null, newRole: false })}
        />
        <Modal.Content>
          <p>
            {confirmRoleChange.newRole
              ? `Are you sure you want to grant admin privileges to ${confirmRoleChange.advisor?.firstName} ${confirmRoleChange.advisor?.lastName}? They will be able to manage other advisors and reassign relationships.`
              : `Are you sure you want to remove admin privileges from ${confirmRoleChange.advisor?.firstName} ${confirmRoleChange.advisor?.lastName}?`}
          </p>
        </Modal.Content>
        <Modal.Actions
          secondaryAction={{ label: 'Cancel', onClick: () => setConfirmRoleChange({ open: false, advisor: null, newRole: false }) }}
          primaryAction={{
            label: confirmRoleChange.newRole ? 'Grant Admin Role' : 'Remove Admin Role',
            onClick: () => {
              if (confirmRoleChange.advisor) void handleRoleChange(confirmRoleChange.advisor, confirmRoleChange.newRole);
            },
            colour: confirmRoleChange.newRole ? 'purple' : 'red',
          }}
        />
      </Modal>

      {/* Reactivate Confirmation */}
      <Modal
        open={confirmReactivate.open}
        onClose={() => setConfirmReactivate({ open: false, advisor: null })}
        size="small"
      >
        <Modal.Header title="Reactivate Advisor" onClose={() => setConfirmReactivate({ open: false, advisor: null })} />
        <Modal.Content>
          <p>Are you sure you want to reactivate {confirmReactivate.advisor?.firstName} {confirmReactivate.advisor?.lastName}?</p>
        </Modal.Content>
        <Modal.Actions
          secondaryAction={{ label: 'Cancel', onClick: () => setConfirmReactivate({ open: false, advisor: null }) }}
          primaryAction={{
            label: 'Reactivate',
            onClick: () => {
              if (confirmReactivate.advisor) void handleReactivate(confirmReactivate.advisor);
            },
            colour: 'green',
          }}
        />
      </Modal>
    </div>
  );
};

export default AdvisorManagementPage;
